// Font resolution for the published form and the builder preview.
//
// The builder's font picker offers six Google families, but the renderer used to
// write the bare family name into --3sb-font ("Inter"). Nothing loaded the
// webfont and nothing followed it in the stack, so every form fell all the way
// back to the browser's default serif — the whole form rendered in Times.
//
// Two things fix that: a real fallback stack behind every choice, and a
// stylesheet link for the families we know Google serves.

/** Followed by every sans-serif choice, and used on its own when nothing is set. */
export const DEFAULT_FONT_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Followed by the serif display choices, so their fallback stays serif. */
const SERIF_FONT_STACK = 'Georgia, "Times New Roman", Times, serif';

interface FontDefinition {
  /** `family=` value for the Google Fonts css2 API — spaces encoded as `+`. */
  googleFamily: string;
  /** Full `font-family` value, chosen family first. */
  stack: string;
}

// Weights match what the stylesheet actually uses: 400 body, 500 labels,
// 600 buttons, 700 heading. Requesting more would cost bytes for nothing.
const WEIGHTS = 'wght@400;500;600;700';

function sans(family: string, urlFamily = family.replace(/ /g, '+')): FontDefinition {
  return { googleFamily: `${urlFamily}:${WEIGHTS}`, stack: `"${family}", ${DEFAULT_FONT_STACK}` };
}

function serif(family: string, urlFamily = family.replace(/ /g, '+')): FontDefinition {
  return { googleFamily: `${urlFamily}:${WEIGHTS}`, stack: `"${family}", ${SERIF_FONT_STACK}` };
}

/**
 * The families the builder's picker offers (`FONT_OPTIONS` in ThemePanel.tsx).
 * Keep the two lists in step: a family missing here still renders, but in the
 * fallback stack rather than the font the author picked.
 */
export const FONT_LIBRARY: Record<string, FontDefinition> = {
  Inter: sans('Inter'),
  Lato: sans('Lato'),
  'Open Sans': sans('Open Sans'),
  Roboto: sans('Roboto'),
  'Playfair Display': serif('Playfair Display'),
  Merriweather: serif('Merriweather'),
};

// A theme's fontFamily is not always one of the six: `extract_theme` scrapes it
// off the charity's own website, so it can be any string that appeared in their
// CSS. Such a family is still honoured — the visitor may well have it installed —
// but only after this filter, and never as part of a URL.
const SAFE_FAMILY = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,63}$/;

/** The `font-family` value to use for a theme's chosen family. */
export function resolveFontStack(fontFamily: string | undefined): string {
  const family = (fontFamily ?? '').trim();
  const known = FONT_LIBRARY[family];
  if (known) return known.stack;
  if (SAFE_FAMILY.test(family)) return `"${family}", ${DEFAULT_FONT_STACK}`;
  return DEFAULT_FONT_STACK;
}

/**
 * The Google Fonts stylesheet URL for one or more families, or null if we serve
 * none of them. A theme can pick a separate heading font, so both families go in
 * one css2 request (it takes repeated `family=` parameters) rather than two.
 */
export function googleFontHref(...fontFamilies: (string | undefined)[]): string | null {
  const googleFamilies = [
    ...new Set(
      fontFamilies
        .map((family) => FONT_LIBRARY[(family ?? '').trim()]?.googleFamily)
        .filter((family): family is string => family !== undefined),
    ),
  ];
  if (googleFamilies.length === 0) return null;
  const query = googleFamilies.map((family) => `family=${family}`).join('&');
  // display=swap so the form's text is readable while the webfont downloads —
  // a donation form must never be invisible waiting on fonts.gstatic.com.
  return `https://fonts.googleapis.com/css2?${query}&display=swap`;
}

/**
 * Points the document at the webfonts for `fontFamilies`, reusing a single link
 * so that retheming in the builder preview swaps the href instead of
 * accumulating a stylesheet per keystroke. When none is a family we serve the
 * link is removed: the stack returned by `resolveFontStack` needs no download.
 */
export function loadWebFont(...fontFamilies: (string | undefined)[]): void {
  const href = googleFontHref(...fontFamilies);
  const existing = document.querySelector<HTMLLinkElement>('link[data-3sb-font]');

  if (!href) {
    existing?.remove();
    return;
  }
  if (existing) {
    if (existing.href !== href) existing.href = href;
    return;
  }

  const link = document.createElement('link');
  link.setAttribute('data-3sb-font', '');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}
